import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { apiCall } from '@/util/api';
import { useAppActiveRef, useAppStateChange } from '@/hooks/useAppState';
import { CONFIG_REFRESH_INTERVAL_MS } from './constants';

export interface AppFeatures {
  betaTesting?: boolean;
  // When falsy (the default), the "Register for Topic" experience is hidden
  // from the TopicView and its route. Enable via FEATURE_FLAGS_JSON.
  topicRegistrationEnabled?: boolean;
  // When falsy (the default), the "news" topic type is disabled: it is not
  // offered in the Create/Update flow and news topics are omitted from the
  // Actions list. Enable via FEATURE_FLAGS_JSON. See
  // @/conditional_feature/news_topic_type_enabled.
  newsTopicTypeEnabled?: boolean;
  // When falsy (the default), all topic types render through the legacy
  // monolithic TopicView. When true, every topic type renders through the
  // per-type React component tree (@/modules/topic_garden). Enable via
  // FEATURE_FLAGS_JSON. See @/conditional_feature/topic_garden_enabled.
  topicGardenEnabled?: boolean;
  // Permanent global ceiling on the number of intros any single topic may send.
  // Sourced from FEATURE_FLAGS_JSON; the intros actually allowed for a topic are
  // min(introCountMax, the topic's own introCountLimit).
  introCountMax?: number;
  // Ceiling on any topic's impressionGoal (#447). A shared config constant
  // that happens to live in FEATURE_FLAGS_JSON — not a conditional feature.
  // The backend defaults to 250 when unset; IMPRESSION_GOAL_MAX_DEFAULT
  // mirrors that client-side.
  impressionGoalMax?: number;
  [key: string]: boolean | number | undefined;
}

interface AppConfig {
  features: AppFeatures;
  // Public Cloudflare Turnstile site key for the contact form. Empty/undefined
  // means captcha is not configured (e.g. local dev) and the widget is skipped.
  turnstileSiteKey?: string;
  isLoaded: boolean;
}

const defaultConfig: AppConfig = {
  features: {},
  turnstileSiteKey: undefined,
  isLoaded: false,
};

const AppConfigContext = createContext<AppConfig>(defaultConfig);

export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isActiveRef = useAppActiveRef();

  const fetchConfig = useCallback(async () => {
    try {
      const response = await apiCall('/api/config');
      if (response.ok) {
        const data = await response.json();
        setConfig({
          features: data.features ?? {},
          turnstileSiteKey: data.turnstileSiteKey || undefined,
          isLoaded: true,
        });
      }
    } catch {
      // Leave previous config in place on network error
      setConfig(prev => ({ ...prev, isLoaded: true }));
    }
  }, []);

  useEffect(() => {
    // Fetch once on mount unconditionally so first paint has config even for a
    // tab that opens in the background, then poll only while the app is active —
    // a hidden/backgrounded tab skips the periodic fetch so idle open tabs don't
    // hammer /api/config.
    fetchConfig();
    intervalRef.current = setInterval(() => {
      if (isActiveRef.current) {
        fetchConfig();
      }
    }, CONFIG_REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchConfig, isActiveRef]);

  // Returning to the foreground refetches immediately so config/flag changes
  // that happened while hidden are picked up without waiting out the interval.
  useAppStateChange(isActive => {
    if (isActive) {
      fetchConfig();
    }
  });

  return (
    <AppConfigContext.Provider value={config}>
      {children}
    </AppConfigContext.Provider>
  );
};

export function useAppConfig(): AppConfig {
  return useContext(AppConfigContext);
}
